import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Card, Form, InputGroup } from "react-bootstrap";
import { Eye, Trash2, Search } from "lucide-react";
import { getLeads, deleteLead } from "../api/lead";
import { toast } from "react-toastify";
import moment from "moment";
import Const from "../utils/constant";
import Modal from "../components/modal";
import { debounce } from "lodash";
import Button from "../components/Buttons";

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await getLeads();
      setLeads(data);
      setFilteredLeads(data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleSearch = useCallback(
    debounce((term) => {
      const filtered = leads.filter(
        (lead) =>
          lead.name?.toLowerCase().includes(term.toLowerCase()) ||
          lead.email?.toLowerCase().includes(term.toLowerCase()) ||
          lead.phone?.includes(term)
      );
      setFilteredLeads(filtered);
    }, 300),
    [leads]
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    handleSearch(e.target.value);
  };

  const openDeleteModal = (lead) => {
    setSelectedLead(lead);
    setIsDeleteModalOpen(true);
  };

  const openViewModal = (lead) => {
    setSelectedLead(lead);
    setIsViewModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedLead) {
      try {
        await deleteLead(selectedLead._id);
        toast.success("Lead deleted successfully");
        fetchLeads();
      } catch (error) {
        toast.error("Failed to delete lead");
      }
    }
  };

  return (
    <div className="container-fluid py-4 min-vh-100 bg-light/30">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1 text-[#1a1a4e]">Leads</h2>
          <p className="text-muted small mb-0">Track and manage customer inquiries from the contact form</p>
        </div>
        <div className="d-flex gap-3 align-items-center">
          <InputGroup className="bg-white rounded-xl overflow-hidden border shadow-sm" style={{ maxWidth: "300px" }}>
            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
              <Search size={18} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="border-0 shadow-none ps-2 py-2"
            />
          </InputGroup>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-3xl overflow-hidden">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 custom-admin-table">
              <thead className="bg-[#1a1a4e] text-white">
                <tr>
                  <th className="px-4 py-3 border-0">Name</th>
                  <th className="py-3 border-0">Email</th>
                  <th className="py-3 border-0">Phone</th>
                  <th className="py-3 border-0">Message Snippet</th>
                  <th className="py-3 border-0 text-center">Date</th>
                  <th className="px-4 py-3 border-0 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : filteredLeads.length > 0 ? (
                  filteredLeads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="fw-semibold text-[#1a1a4e]">{lead.name}</div>
                      </td>
                      <td className="py-3 text-muted">{lead.email}</td>
                      <td className="py-3 text-muted">{lead.phone}</td>
                      <td className="py-3">
                        <div className="text-muted small text-truncate" style={{ maxWidth: "200px" }}>
                          {lead.message}
                        </div>
                      </td>
                      <td className="py-3 text-center text-muted small">
                        {moment(lead.createdAt).format(Const.DATE_TIME_FORMATS.DATE_UI)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            className="p-2 rounded-full text-blue-500 transition-all duration-200 hover:bg-blue-500 hover:text-white"
                            onClick={() => openViewModal(lead)}
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-2 rounded-full text-red-500 transition-all duration-200 hover:bg-red-500 hover:text-white"
                            onClick={() => openDeleteModal(lead)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      {searchTerm ? `No leads found for "${searchTerm}"` : "No leads captured yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Lead"
        desc={`Are you sure you want to delete lead from "${selectedLead?.name}"? This action cannot be undone.`}
        isModalOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        handleSubmit={confirmDelete}
        confirmButtonLabel="Delete Lead"
        cancelButtonLabel="Keep Lead"
        variant="danger"
        size="sm"
      />

      {/* View Lead Details Modal */}
      <Modal
        title="Inquiry Details"
        isModalOpen={isViewModalOpen}
        setIsOpen={setIsViewModalOpen}
        showFooter={false}
        size="md"
      >
        <div>
          <div className="flex justify-between items-center pb-2 border-b border-gray-100">
            <span className="bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
              New Inquiry
            </span>
            <span className="text-xs font-semibold text-gray-800">
              Received: <span className="text-blue-600 font-semibold" >{moment(selectedLead?.createdAt).format("DD MMM YYYY, h:mm a")}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Full Name</label>
              <p className="text-[#1a1a4e] font-semibold text-sm">{selectedLead?.name || "N/A"}</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Phone Number</label>
              <p className="text-gray-800 font-semibold">{selectedLead?.phone || "N/A"}</p>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Email Address</label>
              <p className="text-gray-800 text-sm font-semibold break-all">{selectedLead?.email || "N/A"}</p>
            </div>
          </div>

          <div className="space-y-2 mt-3 ">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block">Message Content</label>
            <div className="bg-[#f8fafc] p-2 rounded-xl border border-[#e2e8f0] text-gray-700 leading-relaxed text-sm whitespace-pre-wrap break-words max-h-[250px] overflow-y-auto min-h-[120px] shadow-inner">{selectedLead?.message || "No message provided."}</div>
          </div>

          <div className="mt-3 flex justify-end">
            <Button onClick={() => setIsViewModalOpen(false)} >Close Inquiry</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Leads;
